import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Rocket, Lock, Shield, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { useTranslation } from "@/contexts/TranslationContext";
import { TranslatedText } from "@/components/TranslatedText";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse delay-700" />

      <div className="relative z-10 container mx-auto px-4 py-20">
        {/* Header */}
        <nav className="flex justify-between items-center mb-20">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg">
              <Shield className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-bold">ServSpect</h1>
          </div>
          <Button variant="ghost" onClick={() => navigate("/admin-access")} className="gap-2">
            <Lock className="h-4 w-4" />
            <TranslatedText text="Admin Login" />
          </Button>
        </nav>

        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6 animate-fade-in">
            <Rocket className="h-4 w-4" />
            <TranslatedText text="Coming Soon" className="text-sm font-medium" />
          </div>

          <h2 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            <TranslatedText text="Server Monitoring" />
            <br />
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              <TranslatedText text="Reimagined" />
            </span>
          </h2>

          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            <TranslatedText text="We are currently building a powerful platform for server monitoring and management. Stay tuned for the launch!" />
          </p>

          <div className="flex gap-4 justify-center">
            <Button size="lg" disabled className="gap-2">
              <TranslatedText text="Sign Up" />
            </Button>
            <Button size="lg" variant="outline" disabled className="gap-2">
              <TranslatedText text="More Information" />
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <Card className="p-6 hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50 bg-card/50 backdrop-blur">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              <TranslatedText text="Real-time Monitoring" />
            </h3>
            <p className="text-muted-foreground">
              <TranslatedText text="Monitor your servers in real-time with advanced metrics and alerts" />
            </p>
          </Card>

          <Card className="p-6 hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50 bg-card/50 backdrop-blur">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              <TranslatedText text="Security First" />
            </h3>
            <p className="text-muted-foreground">
              <TranslatedText text="Enterprise-grade security for your infrastructure monitoring" />
            </p>
          </Card>

          <Card className="p-6 hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50 bg-card/50 backdrop-blur">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Rocket className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              <TranslatedText text="Easy Setup" />
            </h3>
            <p className="text-muted-foreground">
              <TranslatedText text="Install agents in minutes and start monitoring immediately" />
            </p>
          </Card>
        </div>

        {/* Status Banner */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-warning/10 text-warning">
            <div className="w-2 h-2 rounded-full bg-warning animate-pulse" />
            <span className="font-medium">
              <TranslatedText text="In development - Expected launch 2026" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
